import React, { useEffect, useMemo, useRef } from "react";
import type { TFunction } from "i18next";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowRight } from "@fortawesome/pro-regular-svg-icons";
import Popup from "components/popup/Popup";
import FeatureIcon from "components/icon/FeatureIcon";
import { ButtonNew as Button } from "components/buttonNew";
import type {
  IEmailBuilderFinalizeIssue,
  IEmailBuilderFinalizeReview,
  TEmailBuilderFinalizeIssueSeverity,
} from "../../operations/emailBuilderPublishLinkScan";
import "./emailBuilderFinalizeConfirmModal.scss";
import "../emailBuilderCompactPopupActions.scss";

type TEmailBuilderFinalizePendingAction = "finalizeDesign" | "activateTemplate";

type TEmailBuilderFinalizeConfirmModalProps = {
  open: boolean;
  confirming: boolean;
  review: IEmailBuilderFinalizeReview;
  pendingAction: TEmailBuilderFinalizePendingAction | null;
  onClose: () => void;
  onContinue: () => void;
  onGoToBlock: (blockId: string) => void;
};

function isRequiredFinalizeIssue(issue: IEmailBuilderFinalizeIssue): boolean {
  return issue.severity === "error";
}

function finalizeIssueSeverityLabel(t: TFunction, severity: TEmailBuilderFinalizeIssueSeverity): string {
  if (severity === "error") {
    return t("Required");
  }

  if (severity === "info") {
    return t("Info");
  }

  return t("Optional");
}

function finalizeIssueIconProps(severity: TEmailBuilderFinalizeIssueSeverity): {
  type: "error" | "warning" | "gray";
  icon: string;
} {
  if (severity === "error") {
    return { type: "error", icon: "exclamation-triangle" };
  }

  if (severity === "info") {
    return { type: "gray", icon: "question-circle" };
  }

  return { type: "warning", icon: "exclamation" };
}

function finalizeIssueHeading(t: TFunction, issue: IEmailBuilderFinalizeIssue): string {
  if (issue.kind === "unsubscribe") {
    return t("Unsubscribe");
  }

  if (issue.kind === "export") {
    return t(issue.label);
  }

  if (issue.kind === "button") {
    return t("Button link: {{text}}", { text: issue.label });
  }

  if (issue.kind === "image") {
    return t("Image link: {{text}}", { text: issue.label });
  }

  if (issue.kind === "social") {
    return t("Social link: {{text}}", { text: issue.label });
  }

  return t("Link: {{text}}", { text: issue.label });
}

function finalizeIssueGoToAriaLabel(t: TFunction, issue: IEmailBuilderFinalizeIssue): string {
  return t("Go to {{severity}} {{heading}}, {{detail}}", {
    severity: finalizeIssueSeverityLabel(t, issue.severity),
    heading: finalizeIssueHeading(t, issue),
    detail: t(issue.detail),
  });
}

export function EmailBuilderFinalizeConfirmModal(props: TEmailBuilderFinalizeConfirmModalProps) {
  const { t } = useTranslation();
  const returnFocusRef = useRef<HTMLElement | null>(null);
  const issues = props.review.issues;
  const hasIssues = issues.length > 0;
  const hasRequiredIssues = issues.some(isRequiredFinalizeIssue);
  const hasOptionalIssues = issues.some(issue => !isRequiredFinalizeIssue(issue));
  const hideContinueAction = hasRequiredIssues && !props.confirming;

  const popupType = useMemo((): "info" | "warning" | "error" => {
    if (!hasIssues) {
      return "info";
    }

    if (hasRequiredIssues) {
      return "error";
    }

    return "warning";
  }, [hasIssues, hasRequiredIssues]);

  const title = useMemo(() => {
    if (props.pendingAction === "activateTemplate") {
      return hasIssues ? t("Review before saving") : t("Save this template?");
    }

    return hasIssues ? t("Review before finalizing") : t("Finalize this design?");
  }, [hasIssues, props.pendingAction, t]);

  const description = useMemo(() => {
    if (!hasIssues) {
      return props.pendingAction === "activateTemplate"
        ? t("This version will be used when creating new campaigns from this template.")
        : t("This version will be used when the campaign is sent.");
    }

    if (hasRequiredIssues && hasOptionalIssues) {
      return t("Fix required items before continuing. Optional items can be skipped.");
    }

    if (hasRequiredIssues) {
      return t("Fix all required items below before continuing.");
    }

    return t("Optional items below can be fixed now or skipped.");
  }, [hasIssues, hasOptionalIssues, hasRequiredIssues, props.pendingAction, t]);

  const okText = useMemo(() => {
    if (props.confirming) {
      return props.pendingAction === "activateTemplate" ? t("Saving…") : t("Finalizing…");
    }

    if (hasOptionalIssues) {
      return t("Continue anyway");
    }

    return props.pendingAction === "activateTemplate" ? t("Save template") : t("Finalize");
  }, [hasOptionalIssues, props.confirming, props.pendingAction, t]);

  useEffect(() => {
    if (!props.open) {
      return;
    }

    const active = window.document.activeElement;
    returnFocusRef.current = active instanceof HTMLElement ? active : null;
  }, [props.open]);

  useEffect(() => {
    if (props.open) {
      return;
    }

    const returnFocus = returnFocusRef.current;
    if (returnFocus !== null && typeof returnFocus.focus === "function") {
      returnFocus.focus();
    }
    returnFocusRef.current = null;
  }, [props.open]);

  useEffect(() => {
    if (!props.open || props.confirming) {
      return;
    }

    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape") {
        event.preventDefault();
        props.onClose();
      }
    }

    window.document.addEventListener("keydown", handleKeyDown);
    return () => {
      window.document.removeEventListener("keydown", handleKeyDown);
    };
  }, [props.open, props.onClose, props.confirming]);

  function handleClose() {
    if (props.confirming) {
      return;
    }

    props.onClose();
  }

  return (
    <div className="email-builder-finalize-confirm-modal email-builder-compact-popup">
      <Popup
        open={props.open}
        type={popupType}
        title={title}
        description={description}
        closable
        onCancel={handleClose}
        cancelText={t("Cancel")}
        okText={okText}
        onOk={props.onContinue}
        okDisabled={props.confirming}
        hideOk={hideContinueAction}
        cancelDisabled={props.confirming}
        backDropCancel={!props.confirming}
        compactActions
        noFixedWidth
      >
        {hasIssues ? (
          <ul className="email-builder-finalize-confirm-modal__list" role="list">
            {issues.map(issue => {
              const goToLabel = finalizeIssueGoToAriaLabel(t, issue);
              const iconProps = finalizeIssueIconProps(issue.severity);
              const severityLabel = finalizeIssueSeverityLabel(t, issue.severity);
              const heading = finalizeIssueHeading(t, issue);

              return (
                <li
                  key={`${issue.kind}-${issue.label}-${issue.detail}-${issue.blockId ?? "global"}`}
                  className="email-builder-finalize-confirm-modal__item"
                >
                  <span
                    className="email-builder-finalize-confirm-modal__severity"
                    aria-hidden="true"
                  >
                    <FeatureIcon
                      type={iconProps.type}
                      icon={iconProps.icon}
                      theme="light-circle-outline"
                      size="xsmall"
                    />
                  </span>
                  <div className="email-builder-finalize-confirm-modal__item-main">
                    <span className="email-builder-finalize-confirm-modal__label">
                      <span className="email-builder-finalize-confirm-modal__sr-only">
                        {severityLabel}:{" "}
                      </span>
                      {heading}
                    </span>
                    <span className="email-builder-finalize-confirm-modal__detail">
                      {t(issue.detail)}
                    </span>
                  </div>
                  {issue.blockId !== null ? (
                    <Button
                      type="secondary"
                      size="ellipsis"
                      className="email-builder-finalize-confirm-modal__go"
                      disabled={props.confirming}
                      aria-label={goToLabel}
                      title={goToLabel}
                      onClick={() => {
                        if (issue.blockId !== null) {
                          props.onGoToBlock(issue.blockId);
                        }
                      }}
                      icon={<FontAwesomeIcon icon={faArrowRight} aria-hidden="true" />}
                    />
                  ) : null}
                </li>
              );
            })}
          </ul>
        ) : null}
      </Popup>
    </div>
  );
}
